import React, { useEffect, useState } from "react"
import { actualizarConsentimientoMeta } from "utils/metaPixel"
import { darseDeBajaPush, pushSoportado } from "utils/pushNotificaciones"

/* Consentimiento de cookies. El valor por defecto (todo lo no esencial denegado)
   se fija en public/index.html antes de cargar GTM; aquí solo se actualiza. */
export const CLAVE_CONSENTIMIENTO = "tickets_cookies_consent"
export const EVENTO_ABRIR_PREFERENCIAS = "tickets:abrir-cookies"
export const URL_POLITICA_PRIVACIDAD = "https://t-ickets.com/politica-privacidad-tickets.html"

const leerConsentimiento = () => {
  try { return JSON.parse(localStorage.getItem(CLAVE_CONSENTIMIENTO)) } catch (e) { return null }
}

const aplicarConsentimiento = (analitica) => {
  const v = analitica ? "granted" : "denied"
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: v, ad_user_data: v, ad_personalization: v, analytics_storage: v,
    })
  }
}

export const abrirPreferenciasCookies = () => window.dispatchEvent(new Event(EVENTO_ABRIR_PREFERENCIAS))

export const ConsentimientoCookies = () => {
  const [visible, setVisible] = useState(() => !leerConsentimiento())

  useEffect(() => {
    const abrir = () => setVisible(true)
    window.addEventListener(EVENTO_ABRIR_PREFERENCIAS, abrir)
    return () => window.removeEventListener(EVENTO_ABRIR_PREFERENCIAS, abrir)
  }, [])

  const guardar = (analitica) => {
    try {
      localStorage.setItem(CLAVE_CONSENTIMIENTO, JSON.stringify({ analitica, fecha: new Date().toISOString() }))
    } catch (e) { }
    aplicarConsentimiento(analitica)
    // Pixel de Meta: se carga solo si acepta; si rechaza, Meta deja de usar sus datos
    actualizarConsentimientoMeta(analitica)
    setVisible(false)
  }

  if (!visible) return null
  return (
    <div role="dialog" aria-live="polite" aria-label="Consentimiento de cookies"
      style={{
        position: "fixed", left: 12, right: 12, bottom: 12, zIndex: 1030, maxWidth: 720, margin: "0 auto",
        background: "#ffffff", color: "#1a1e52", borderRadius: 12, padding: "16px 18px",
        boxShadow: "0 6px 28px rgba(0,0,0,0.25)", fontSize: "0.9em", lineHeight: 1.45,
      }}>
      <p className="mb-2">
        <b>Usamos cookies.</b> Las cookies necesarias permiten que el sitio funcione (inicio de sesión, carrito de compra y pago).
        Con tu permiso, también usamos cookies de analítica y publicidad (Google Analytics / Google Tag Manager y el Pixel de Meta) para medir
        el uso del sitio y mejorar nuestros servicios. Puedes cambiar tu decisión en cualquier momento desde el pie de página.
      </p>
      <p className="mb-3" style={{ fontSize: "0.9em" }}>
        Más información en nuestra{" "}
        <a href="#!" data-toggle="modal" data-target="#cookiesModal" style={{ color: "#2d358c", fontWeight: 600 }}>Política de cookies</a>
        {" "}y{" "}
        <a href={URL_POLITICA_PRIVACIDAD} target="_blank" rel="noopener noreferrer" style={{ color: "#2d358c", fontWeight: 600 }}>Política de privacidad</a>.
      </p>
      <div className="d-flex flex-wrap justify-content-end" style={{ gap: 8 }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => guardar(false)}>
          Solo necesarias
        </button>
        <button type="button" className="btn btn-sm text-white" style={{ background: "#311C7C" }} onClick={() => guardar(true)}>
          Aceptar todas
        </button>
      </div>
    </div>
  )
}

// Retirar las notificaciones push de este navegador (el permiso tambien se puede quitar desde la configuracion del navegador)
const ControlNotificaciones = () => {
  const [estado, setEstado] = useState("")
  if (!pushSoportado() || window.Notification.permission !== "granted") return null
  const desactivar = async () => {
    const r = await darseDeBajaPush()
    setEstado(r.ok ? "Listo: ya no recibirás notificaciones en este navegador." : "No se pudo desactivar. Quítalas desde la configuración de tu navegador.")
  }
  return (
    <p>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={desactivar}>Desactivar notificaciones en este navegador</button>
      {estado ? <span className="ml-2">{estado}</span> : null}
    </p>
  )
}

export const ModalCookies = () => (
  <div className="modal fade" id="cookiesModal" role="dialog" aria-labelledby="cookiesModalLabel" aria-hidden="true">
    <div className="modal-dialog modal-lg" role="document">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title text-dark" id="cookiesModalLabel">POLÍTICA DE COOKIES</h5>
          <button type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">x</span>
          </button>
        </div>
        <div className="modal-body text-dark" style={{ maxHeight: 600, overflowY: "auto", fontSize: "0.92em" }}>
          <p>
            Esta política explica cómo TICKETSECUADOR S.A. (T-ICKETS), RUC 0993377293001, utiliza cookies y tecnologías
            similares en www.t-ickets.com, conforme a la Ley Orgánica de Protección de Datos Personales del Ecuador.
          </p>
          <h6 className="font-weight-bold">1. ¿Qué son las cookies?</h6>
          <p>
            Son pequeños archivos que el sitio guarda en tu navegador para recordar información sobre tu visita.
            También usamos el almacenamiento local del navegador (localStorage / sessionStorage) con el mismo fin.
          </p>
          <h6 className="font-weight-bold">2. Cookies que utilizamos</h6>
          <ul>
            <li>
              <b>Necesarias (siempre activas):</b> mantienen tu sesión iniciada, el carrito de compra, los asientos
              seleccionados, el proceso de pago y tu decisión sobre cookies. Sin ellas el sitio no funciona correctamente.
            </li>
            <li>
              <b>Analítica (requieren tu consentimiento):</b> Google Analytics y Google Tag Manager, para conocer cuántas
              personas visitan el sitio, qué páginas usan y cómo mejorar el servicio. La información es estadística.
            </li>
            <li>
              <b>Publicidad (requieren tu consentimiento):</b> etiquetas gestionadas por Google Tag Manager y el Pixel de
              Meta (Facebook / Instagram), que permiten medir campañas y mostrar anuncios relevantes de nuestros eventos.
              Si compras, a Meta se le informa la compra con tu correo, teléfono y cédula cifrados (no legibles), solo
              para medir los anuncios.
            </li>
            <li>
              <b>Notificaciones (requieren tu permiso):</b> si aceptas el aviso que te mostramos al ver un concierto o comprar, y luego
              confirmas en tu navegador, te enviamos notificaciones sobre ese concierto (novedades y últimas entradas), promociones y, si
              compraste, el aviso de que tus boletos están listos. Para esto guardamos un identificador técnico de tu navegador y, si compras,
              tu número de cédula asociado a esa compra. No usamos este permiso para nada más y no lo compartimos con terceros distintos del
              servicio de notificaciones de tu navegador (Google, Mozilla, Apple o Microsoft), que solo transporta el mensaje.
            </li>
            <li>
              <b>De terceros en el pago:</b> las pasarelas de pago (PagoPlux, Payphone, Deuna) pueden usar sus propias
              cookies cuando realizas un pago en sus páginas, bajo sus propias políticas.
            </li>
          </ul>
          <h6 className="font-weight-bold">3. Cómo gestionar tus preferencias</h6>
          <p>
            Al entrar por primera vez te mostramos un aviso donde puedes elegir “Aceptar todas” o “Solo necesarias”.
            Puedes cambiar tu decisión en cualquier momento desde el enlace “Configurar cookies” en el pie de página.
            También puedes borrar o bloquear las cookies desde la configuración de tu navegador; si bloqueas las
            necesarias, algunas funciones (como comprar) pueden dejar de funcionar.
          </p>
          <p>
            <b>Notificaciones:</b> nunca se activan sin tu permiso. Si dices “Ahora no”, volveremos a preguntarte con más espera cada vez
            (2, 7 y 30 días) y, tras la cuarta negativa, no te preguntaremos más. Puedes retirar tu permiso cuando quieras: con el botón de
            abajo (si lo tienes activo en este navegador), desde el candado junto a la dirección del sitio o desde la configuración de
            notificaciones de tu navegador.
          </p>
          <ControlNotificaciones />
          <h6 className="font-weight-bold">4. Conservación</h6>
          <p>
            Las cookies de sesión se eliminan al cerrar el navegador. Tu decisión sobre cookies se guarda hasta que la
            cambies o borres los datos del navegador. Las cookies de Google Analytics duran hasta 2 años y las del Pixel de Meta hasta 90 días. Tu suscripción a
            notificaciones se conserva hasta que la retires o hasta que tu navegador deje de aceptarla; si los avisos dejan de poder entregarse a tu navegador, la desactivamos.
          </p>
          <h6 className="font-weight-bold">5. Cambios y contacto</h6>
          <p className="mb-0">
            Podemos actualizar esta política; la versión vigente estará siempre publicada en este sitio. Para consultas
            escríbenos por WhatsApp al +593 98 000 9000 o a ventas@t-ickets.com. Más detalles sobre el tratamiento de tus
            datos en nuestra{" "}
            <a href={URL_POLITICA_PRIVACIDAD} target="_blank" rel="noopener noreferrer">Política de privacidad</a>.
          </p>
        </div>
      </div>
    </div>
  </div>
)
